/*
  Shared helpers for the omes quick view (sections/omes-quick-view.liquid) and
  the omes quick buy drawer (sections/omes-quick-buy.liquid).

  Both render themselves from /products/{handle}.js, so both need the same two
  things solved in the browser:

  1. colour → image. Shopify only links a variant to an image when a merchant
     assigns one, and products imported by the onboarding pipeline have no
     variant→image link at all (AGENTS.md §12.5) — `variant.featured_image` is
     null across most of the catalogue. That is why picking a colour changed the
     price but left the picture alone. Resolution mirrors the PDP
     (sections/omes-main-product.liquid): variant image → media alt names the
     colour → positional, when there is exactly one image per colour.

  2. swatch colour. Both sections shipped their own small English/French hex
     tables that disagreed with the storefront's curated palette and fell back
     to a grey chip. They now read the same map the PDP, the collection cards
     and the Avone swatches resolve through — snippets/omes-color-hex.liquid,
     whose table lives in snippets/omes-color-hex-data.liquid and is emitted
     into the page by snippets/omes-color-hex-json.liquid.
*/
(function () {
  'use strict';

  var hexMap = null;

  function parseHexMap() {
    if (hexMap) return hexMap;
    hexMap = {};
    var el = document.querySelector('[data-omes-color-hex]');
    if (!el) return hexMap;
    // Serialized as |name:#hex|name:#hex| — names contain spaces, hexes don't
    // contain pipes, so a pipe split is unambiguous.
    el.textContent.split('|').forEach(function (pair) {
      var at = pair.indexOf(':');
      if (at <= 0) return;
      hexMap[pair.slice(0, at).trim()] = pair.slice(at + 1).trim();
    });
    return hexMap;
  }

  function normalise(value) {
    return (value || '').toLowerCase().trim();
  }

  /* Materials vendors prefix a colour with — "Daim cognac", "Cuir grainé noir".
     `denim` is a material AND a colour in the table, so it is not listed. */
  var MATERIALS = /^(daim|cuirs?|nubuck|velours|satins?|satin[ée]|su[eè]de|su[ée]d[ée]|toile|tissu|grain[ée]|granul[ée]|vernis?|paillet[ée]|m[ée]tallis[ée])$/;

  /* Mirrors steps 3a–3e of snippets/omes-color-hex.liquid. */
  function hexFor(value) {
    var map = parseHexMap();
    var name = normalise(value);
    if (!name) return null;

    // 3a) exact match on the whole value
    if (map[name]) return map[name];

    // 3b) drop material / finish words. Every step below reduces from the right,
    // so without this "daim cognac" collapses to "daim" and loses the colour.
    // Runs before the compound split so "cuir blanc et daim vert" becomes
    // "blanc et vert" and still renders two-tone. Left untouched if every word
    // is a material.
    var kept = name.replace(/[~\-]/g, ' ').split(/\s+/)
      .filter(function (w) { return w && !MATERIALS.test(w); }).join(' ');
    if (kept) name = kept;
    if (map[name]) return map[name];

    // 3c) compound value ("Camel et doré", "Noir/Blanc") → two known tones side
    // by side. Runs before the progressive drop, which would otherwise collapse
    // "camel et doré" to plain "camel".
    var tones = [];
    name.split(/\s+et\s+|\s+and\s+|\/|\s+&\s+|\s\+\s|\s-\s|,/).forEach(function (token) {
      var hex = map[token.trim()];
      if (hex && tones.length < 2) tones.push(hex);
    });
    if (tones.length === 2) {
      return 'linear-gradient(135deg, ' + tones[0] + ' 0%, ' + tones[0] + ' 50%, ' +
        tones[1] + ' 50%, ' + tones[1] + ' 100%)';
    }
    if (tones.length === 1) return tones[0];

    // 3d) progressive match: drop trailing tokens so messy names still land on a
    // sensible base colour ("bleu marine r" → "bleu marine").
    var words = name.replace(/[~\-/.,]/g, ' ').trim().split(/\s+/);
    for (var take = words.length; take > 0; take--) {
      var candidate = words.slice(0, take).join(' ');
      if (map[candidate]) return map[candidate];
    }

    // 3e) backstop for prefixes 3b doesn't know about: scan the inner spans too,
    // earliest start first, longest span first ("xxx bleu ciel" → "bleu ciel").
    for (var start = 1; start < words.length; start++) {
      for (var end = words.length; end > start; end--) {
        var inner = words.slice(start, end).join(' ');
        if (map[inner]) return map[inner];
      }
    }

    return null;
  }

  function isMulticolor(value) {
    var name = normalise(value).replace(/-/g, ' ');
    return name === 'multicolore' || name === 'multi' || name === 'multicolor' || name === 'multicolour';
  }

  /* Swatch background for an option value: a hex, a gradient, or null. */
  function swatchBackground(value) {
    var hex = hexFor(value);
    if (isMulticolor(value) && (!hex || hex.toUpperCase() === '#A0A0A0')) {
      return 'linear-gradient(45deg,#C62828,#FB8C00,#FFEB3B,#2E7D32,#1976D2,#6A1B9A)';
    }
    return hex;
  }

  function withoutProtocol(url) {
    return (typeof url === 'string' ? url : '').replace(/^https?:/, '').split('?')[0];
  }

  /* product.images, but only the entries an index can point at. */
  function imageAlts(product) {
    if (!product.media) return [];
    return product.media
      .filter(function (m) { return m.media_type === 'image'; })
      .map(function (m) { return normalise(m.alt); });
  }

  /*
    Index into product.images for a colour value, or -1 when nothing resolves —
    in which case the caller leaves the gallery where it is.
  */
  function colorImageIndex(product, colorIdx, value) {
    if (!product || !product.images || colorIdx < 0 || !value) return -1;
    var images = product.images.map(withoutProtocol);

    // 1) a variant of that colour that does have an image assigned
    var variant = (product.variants || []).find(function (v) {
      return v.options[colorIdx] === value && v.featured_image;
    });
    if (variant) {
      var src = typeof variant.featured_image === 'string'
        ? variant.featured_image
        : variant.featured_image.src;
      var found = images.indexOf(withoutProtocol(src));
      if (found >= 0) return found;
    }

    // 2) alt text names the colour — exact, then contains
    var needle = normalise(value);
    var alts = imageAlts(product);
    var exact = alts.indexOf(needle);
    if (exact >= 0) return exact;
    for (var i = 0; i < alts.length; i++) {
      if (alts[i] && alts[i].indexOf(needle) !== -1) return i;
    }

    // 3) one image per colour → same order. The scraper uploads a single image
    // per colour in option order, so this is the case that actually carries the
    // catalogue; anything else is too speculative to guess at.
    var values = [];
    (product.variants || []).forEach(function (v) {
      var val = v.options[colorIdx];
      if (val && values.indexOf(val) === -1) values.push(val);
    });
    if (values.length > 1 && values.length === product.images.length) {
      return values.indexOf(value);
    }

    return -1;
  }

  window.OmesQuick = {
    hexFor: hexFor,
    swatchBackground: swatchBackground,
    colorImageIndex: colorImageIndex
  };
})();
